import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@mv-submodules/inplant-components-fe'
import IconComponent from '../../../../../inplant-components-fe/ui/components/MVIcon/Icon'
import { PendingUpload } from '../../../../redux/reducers/checklistEdit'
import { uploadProgress } from '../../../../functions/upload'
import { useDispatchEditChecklist, usePendingUploads } from '../../../../hook/useEditChecklist'

const PROGRESS_REFRESH_MS = 250

/**
 * The uploads (queued, in progress or failed) of the component with id {@link componentId}.
 */
export function PendingUploads({ componentId }: { componentId: string }) {
  const uploads = usePendingUploads(componentId)
  const { dismissFailedUpload, retryFailedUpload } = useDispatchEditChecklist()

  return (
    <>
      {uploads.map(upload => (
        <UploadTile key={upload.id} upload={upload} onDismiss={dismissFailedUpload} onRetry={retryFailedUpload} />
      ))}
    </>
  )
}

/**
 * Sending the same file again can't fix these errors: a new photo is needed.
 */
const NOT_RETRYABLE_ERRORS: Array<PendingUpload['errorKind']> = ['exifTooOld', 'exifMissing', 'notImage']

/**
 * Preview of a photo being uploaded with a time-based progress bar, or the reason why the upload failed.
 *
 * While the upload is verified the tile looks exactly like during the upload: the user sees only the final outcome.
 */
export function UploadTile({
  upload,
  onDismiss,
  onRetry,
}: {
  upload: PendingUpload
  onDismiss: (uploadId: string) => void
  onRetry: (uploadId: string) => void
}) {
  const { t } = useTranslation()
  const previewUrl = useObjectUrl(upload.file)
  const progress = useUploadProgress(upload)
  const failed = upload.phase === 'failed'
  const retryable = failed && !NOT_RETRYABLE_ERRORS.includes(upload.errorKind)

  return (
    <div className={`multi-file-thumb upload-tile ${failed ? 'upload-tile-failed' : ''}`}>
      {retryable ? (
        <span className="upload-tile-retry">
          <Button variant={'secondary'} onClick={() => onRetry(upload.id)} icon={'redo'} />
        </span>
      ) : null}
      {failed ? <Button variant={'secondary'} onClick={() => onDismiss(upload.id)} icon={'times'} /> : null}
      <img className="img-fluid mx-auto d-block img-thumbnail" src={previewUrl} />
      {failed ? (
        <div className="upload-tile-status text-danger">
          <div className="upload-tile-status-title">
            <IconComponent icon={'exclamation-triangle'} /> {t('checklist.components.upload.failed')}
          </div>
          {t(`checklist.components.upload.errors.${upload.errorKind ?? 'server'}`)}
        </div>
      ) : (
        <>
          <div className="progress upload-tile-progress">
            <div
              className="progress-bar progress-bar-striped progress-bar-animated"
              role="progressbar"
              style={{ width: `${progress}%` }}
              aria-valuenow={Math.round(progress)}
              aria-valuemin={0}
              aria-valuemax={100}
            />
          </div>
          <div className="upload-tile-status">
            {t(upload.phase === 'queued' ? 'checklist.components.upload.queued' : 'checklist.components.upload.uploading')}
          </div>
        </>
      )}
    </div>
  )
}

/**
 * Badge shown on the photos saved on the server.
 */
export function SavedBadge() {
  const { t } = useTranslation()

  return (
    <span className="upload-saved-badge" title={t('checklist.components.upload.saved')}>
      <IconComponent icon={'check'} /> {t('checklist.components.upload.saved')}
    </span>
  )
}

/**
 * An object URL of {@link file}, stable across renders and revoked when no longer used.
 */
export function useObjectUrl(file: Blob): string {
  const url = useMemo(() => URL.createObjectURL(file), [file])

  useEffect(() => () => URL.revokeObjectURL(url), [url])

  return url
}

function useUploadProgress(upload: PendingUpload): number {
  const [now, setNow] = useState(Date.now())
  const running = upload.phase === 'uploading' || upload.phase === 'verifying'

  useEffect(() => {
    if (!running) {
      return
    }

    const interval = setInterval(() => setNow(Date.now()), PROGRESS_REFRESH_MS)
    return () => clearInterval(interval)
  }, [running])

  return running ? uploadProgress(upload.startedAt, now) : 0
}
